const DEFAULT_VIDEO_BITS_PER_SECOND = 200_000;
const DEFAULT_MIME_TYPE = 'video/webm';

interface StartRecordingOptions {
  chunkDuration?: number;
  preferredMimeTypes?: string[];
  videoBitsPerSecond?: number;
}

interface CaptureInfo {
  displaySurface: string;
  width: number;
  height: number;
  isCapturingWindowOrMonitor: boolean;
}

interface ExtendedMediaTrackSettings extends MediaTrackSettings {
  displaySurface?: string;
}

class ScreenRecorder {
  private screenTracks: MediaStreamTrack[] = [];
  private recorder: MediaRecorder | null = null;
  private currentStream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private canvasElement: HTMLCanvasElement | null = null;

  onscreenchunk: ((blob: Blob) => void) | undefined;
  onCaptureTypeDetected: ((info: CaptureInfo) => void) | undefined;

  async acquireStream(): Promise<MediaStream> {
    if (this.currentStream) {
      return this.currentStream;
    }

    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'window',
        },
        audio: true,
      });

      this.screenTracks = screenStream.getTracks();
      this.currentStream = screenStream;

      const captureInfo = this.analyzeCaptureType();
      if (this.onCaptureTypeDetected && captureInfo) {
        this.onCaptureTypeDetected(captureInfo);
      }

      // Handle user stopping screen share
      this.screenTracks.forEach((track) => {
        track.onended = () => {
          this.stopRecording();
        };
      });

      this.videoElement = document.createElement('video');
      this.videoElement.srcObject = screenStream;
      this.videoElement.muted = true;
      this.videoElement.play();
      this.canvasElement = document.createElement('canvas');

      return screenStream;
    } catch (error) {
      console.error('Error acquiring screen stream:', error);
      this.currentStream = null;
      this.screenTracks = [];
      throw error;
    }
  }

  startRecording(
    {
      chunkDuration = 1000,
      preferredMimeTypes,
      videoBitsPerSecond,
    }: StartRecordingOptions = {},
    audioStream?: MediaStream,
  ) {
    if (!this.currentStream) {
      throw new Error('Screen stream not available. Call acquireStream first.');
    }

    try {
      let supportedMimeType = DEFAULT_MIME_TYPE;
      for (const mimeType of preferredMimeTypes || []) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          supportedMimeType = mimeType;
          break;
        }
      }

      const videoTracks = this.currentStream.getVideoTracks();
      const audioTracks = audioStream ? audioStream.getAudioTracks() : [];
      const systemAudioTracks = this.currentStream.getAudioTracks();
      const combinedStream = new MediaStream([
        ...videoTracks,
        ...audioTracks,
        ...systemAudioTracks,
      ]);

      this.recorder = new MediaRecorder(combinedStream, {
        mimeType: supportedMimeType,
        videoBitsPerSecond: videoBitsPerSecond ?? DEFAULT_VIDEO_BITS_PER_SECOND,
      });

      this.recorder.start(chunkDuration);

      this.recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          this.onscreenchunk?.(event.data);
        }
      };
    } catch (error) {
      console.error('Error starting screen recording:', error);
      throw error;
    }
  }

  stopRecording(): Promise<void> {
    this.screenTracks.forEach((track) => {
      track.stop();
    });
    this.screenTracks = [];
    this.currentStream = null;

    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.srcObject = null;
    }
    this.videoElement = null;
    this.canvasElement = null;

    const recorder = this.recorder;
    if (recorder) {
      this.recorder = null;
      return new Promise((resolve) => {
        if (recorder.state === 'inactive') {
          resolve();
          return;
        }
        recorder.onstop = () => {
          resolve();
        };
        recorder.stop();
      });
    }

    return Promise.resolve();
  }

  analyzeCaptureType(): CaptureInfo | null {
    if (!this.currentStream) return null;

    const videoTrack = this.currentStream.getVideoTracks()[0];
    if (!videoTrack) {
      return null;
    }

    const settings = videoTrack.getSettings() as ExtendedMediaTrackSettings;
    const displaySurface = settings.displaySurface || 'unknown';
    const width = settings.width || 0;
    const height = settings.height || 0;
    const isCapturingWindowOrMonitor =
      settings.displaySurface === 'window' ||
      settings.displaySurface === 'monitor';

    return {
      displaySurface,
      width,
      height,
      isCapturingWindowOrMonitor,
    };
  }

  destroy() {
    this.stopRecording();
  }

  isRecording(): boolean {
    return this.recorder !== null && this.recorder.state === 'recording';
  }

  async captureFrame(
    format: 'image/jpeg' | 'image/png' = 'image/jpeg',
    quality = 0.8,
  ): Promise<string | null> {
    if (!this.currentStream || !this.videoElement || !this.canvasElement) {
      return null;
    }

    const videoTrack = this.currentStream.getVideoTracks()[0];
    if (!videoTrack) {
      return null;
    }

    const { width, height } = videoTrack.getSettings();
    if (!width || !height) {
      return null;
    }

    if (this.canvasElement.width !== width) {
      this.canvasElement.width = width;
    }
    if (this.canvasElement.height !== height) {
      this.canvasElement.height = height;
    }

    const context = this.canvasElement.getContext('2d');
    if (!context) {
      return null;
    }

    context.drawImage(this.videoElement, 0, 0, width, height);

    return this.canvasElement.toDataURL(format, quality);
  }
}

export default ScreenRecorder;
